import { patchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import type { ProfileDisplaySummary } from './profileDisplaySummary';
import { persistProfileDetailsHints } from './profileDetailsStorage';
import { normalizePhoneForWire } from './phoneE164';
import {
  isProfilePatchIdentityConflictError,
  SIGNED_IN_PROFILE_COMPLETION_OMIT_KEYS,
} from './profileCompletionPatch';
import { sanitizeProfilePatchBody } from './profilePrivateClientPatch';
import type { ProfileVehicle } from './profileVehicles';
import { clearStoredProfileVehicles } from './profileVehiclesStorage';
import { recordProfileUpdatedFromPatchResponse } from './profileLastUpdated';

export { sanitizeProfilePatchBody } from './profilePrivateClientPatch';

export type ProfileEditSavedFields = {
  firstName: string;
  lastName: string;
  occupation: string;
  birthDate: string;
  vehicles?: ProfileVehicle[];
};

/** Keep values the user just saved when GET /profile still omits them after PATCH. */
export function mergeSavedProfileEditFields(
  summary: ProfileDisplaySummary,
  saved: ProfileEditSavedFields,
): ProfileDisplaySummary {
  const firstName = summary.firstName?.trim() || saved.firstName.trim() || null;
  const lastName = summary.lastName?.trim() || saved.lastName.trim() || null;
  const occupation = summary.occupation?.trim() || saved.occupation.trim() || null;
  const dateOfBirth = summary.dateOfBirth?.trim() || saved.birthDate.trim() || null;
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  const displayName = joined || summary.displayName?.trim() || '';
  // Prefer server vehicles after refresh; form values only if GET still omitted them.
  const vehicles =
    summary.vehicles.length > 0 || saved.vehicles === undefined
      ? summary.vehicles
      : saved.vehicles;

  return {
    ...summary,
    firstName,
    lastName,
    occupation,
    dateOfBirth,
    displayName,
    vehicles,
  };
}

export function persistProfileEditSavedFields(saved: ProfileEditSavedFields, loginId?: string | null): void {
  persistProfileDetailsHints(
    {
      firstName: saved.firstName,
      lastName: saved.lastName,
      occupation: saved.occupation,
      birthDate: saved.birthDate,
    },
    loginId ?? getStoredLoginIdentifier(),
  );
  // Vehicles are persisted by PATCH /profile — clear any legacy local cache.
  if (saved.vehicles !== undefined) {
    clearStoredProfileVehicles(loginId ?? getStoredLoginIdentifier());
  }
}

export function buildProfileEditPatchBody(
  fields: ProfileEditSavedFields & {
    profileImage?: string;
    logo?: string;
    email?: string;
    phoneNumber?: string;
  },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    first_name: fields.firstName.trim(),
    last_name: fields.lastName.trim(),
    occupation: fields.occupation.trim(),
  };
  const dob = fields.birthDate.trim();
  if (dob) {
    body.birth_date = dob;
  }
  if (fields.email?.trim()) {
    body.email = fields.email.trim();
  }
  if (fields.phoneNumber !== undefined) {
    body.phone_number = normalizePhoneForWire(fields.phoneNumber);
  }
  if (fields.profileImage?.trim()) {
    body.profile_image = fields.profileImage.trim();
  }
  if (fields.logo?.trim()) {
    body.logo = fields.logo.trim();
  }
  return sanitizeProfilePatchBody(body);
}

/** Signed-in PATCH /profile — never re-send email (backend 409 duplicate). */
export function buildSignedInProfileEditPatchBody(
  fields: Omit<ProfileEditSavedFields, 'vehicles'> & {
    profileImage?: string;
    logo?: string;
    phoneNumber?: string;
    /** Ignored on wire — included only so callers can reuse form state; stripped before PATCH. */
    email?: string;
    vehicles?: unknown;
  },
): Record<string, unknown> {
  const { vehicles, ...patchFields } = fields;
  const body = buildProfileEditPatchBody(patchFields);
  for (const key of SIGNED_IN_PROFILE_COMPLETION_OMIT_KEYS) {
    delete body[key];
  }
  if (vehicles !== undefined) {
    body.vehicles = vehicles;
  }
  return body;
}

function assertNonEmptyProfilePatchBody(body: Record<string, unknown>): void {
  if (Object.keys(body).length === 0) {
    throw new Error('Nothing to save on the server.');
  }
}

/**
 * PATCH /profile for a signed-in private client. Omits email; retries without phone on 409.
 * Must run before persisting name hints locally so other devices see the update.
 */
export async function patchSignedInProfileEdit(body: Record<string, unknown>): Promise<void> {
  let patchBody = sanitizeProfilePatchBody({ ...body });
  for (const key of SIGNED_IN_PROFILE_COMPLETION_OMIT_KEYS) {
    delete patchBody[key];
  }
  assertNonEmptyProfilePatchBody(patchBody);

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info('[patchSignedInProfileEdit] PATCH /profile', patchBody);
  }

  try {
    const response = await patchProfile(patchBody);
    recordProfileUpdatedFromPatchResponse(response, getStoredLoginIdentifier());
    return;
  } catch (err) {
    if (!isProfilePatchIdentityConflictError(err) || !('phone_number' in patchBody)) {
      throw err;
    }
    const { phone_number: _phone, ...withoutPhone } = patchBody;
    assertNonEmptyProfilePatchBody(withoutPhone);
    const response = await patchProfile(withoutPhone);
    recordProfileUpdatedFromPatchResponse(response, getStoredLoginIdentifier());
  }
}
